import React, {useMemo} from "react"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component"
import AdminButton from "components/admin/button"
import AdminInput from "components/admin/input"
import {AdminSelectContactTypes} from "components/contact-types/select"
import {AdminSelectCountries} from "components/countries/select"
import {AdminSelectCountryStates} from "components/country-states/select"
import {AdminSelectUsers} from "components/users/select"
import AppHistory from "shared/history"
import {Contact} from "models"
import {FlashNotifications as FlashMessage} from "flash-notifications"
import {Form} from "@kaspernj/api-maker/build/form"
import memo from "set-state-compare/build/memo"
import presence from "shared/presence"
import Routes from "shared/routes"
import {useAdminLayout} from "components/admin/layout"
import useI18n from "i18n-on-steroids/src/use-i18n.mjs"
import useModel from "@kaspernj/api-maker/build/use-model"
import {v4 as uuidv4} from "uuid"
import {View} from "react-native"

export default memo(shapeComponent(class RoutesAdminContactsEdit extends ShapeComponent {
  setup() {
    const {t} = useI18n({namespace: "js.routes.admin.contacts.edit"})
    const {contact, contactId} = useModel(Contact, {
      match: this.p.match,
      newIfNoId: {
        defaults: () => ({id: uuidv4()})
      },
      preload: ["contact_type", "user"],
      select: {
        Contact: ["accountManagerId", "contactTypeId", "countryId", "countryStateId", "firstName", "id", "lastName", "name", "phoneNumber"],
        ContactType: ["id", "identifier"]
      }
    })

    this.setInstance({contact, contactId, t})
    this.useStates({
      contactTypeIdentifier: null,
      form: null
    })

    useMemo(() => {
      if (contact) {
        this.setState({
          contactTypeIdentifier: contact.contactType()?.identifier() || null
        })
      }
    }, [contact?.id()])

    useAdminLayout()?.setState({active: "contacts", headerTitle: this.headerTitle()})
  }

  render() {
    const {contact} = this.tt
    const {contactTypeIdentifier} = this.s

    return (
      <View dataSet={this.rootViewDataSet ||= {route: "admin/contacts/edit"}}>
        {contact &&
          <Form onSubmit={this.tt.onSubmit} setForm={this.setStates.form}>
            <View style={{marginTop: 10, marginBottom: 20}}>
              <AdminSelectContactTypes
                attribute="contactTypeId"
                id="contact_contact_type_id"
                label={Contact.humanAttributeName("contactType")}
                model={contact}
                name="contact[contact_type_id]"
                onChange={this.tt.onContactTypeChange}
              />
            </View>
            {contactTypeIdentifier != "person" &&
              <AdminInput
                attribute="name"
                dataSet={{class: "contact-name-input"}}
                label={Contact.humanAttributeName("name")}
                model={contact}
                name="contact[name]"
                style={this.contactNameInputStyle ||= {marginBottom: 10}}
              />
            }
            {contactTypeIdentifier == "person" &&
              <>
                <AdminInput
                  attribute="firstName"
                  dataSet={{class: "contact-first-name-input"}}
                  label={Contact.humanAttributeName("firstName")}
                  model={contact}
                  name="contact[first_name]"
                  style={this.contactFirstNameInputStyle ||= {marginBottom: 10}}
                />
                <AdminInput
                  attribute="lastName"
                  dataSet={{class: "contact-last-name-input"}}
                  label={Contact.humanAttributeName("lastName")}
                  model={contact}
                  name="contact[last_name]"
                  style={this.contactLastNameInputStyle ||= {marginBottom: 10}}
                />
              </>
            }
            {contactTypeIdentifier != "school_group" &&
              <View style={{marginVertical: 10}}>
                <AdminSelectUsers
                  attribute="accountManagerId"
                  id="contact_account_manager_id"
                  label={Contact.humanAttributeName("accountManager")}
                  model={contact}
                  name="contact[account_manager_id]"
                />
              </View>
            }
            <View style={{marginVertical: 10}}>
              <AdminSelectCountries
                attribute="countryId"
                id="contact_country_id"
                label={Contact.humanAttributeName("country")}
                model={contact}
                name="contact[country_id]"
              />
            </View>
            <View style={{marginVertical: 10}}>
              <AdminSelectCountryStates
                attribute="countryStateId"
                id="contact_country_state_id"
                label={Contact.humanAttributeName("countryState")}
                model={contact}
                name="contact[country_state_id]"
              />
            </View>
            <AdminButton primary save submit />
          </Form>
        }
      </View>
    )
  }

  headerTitle() {
    const {contact, t} = this.tt

    if (contact?.isNewRecord()) {
      return t(".add_new_contact")
    } else if (contact?.isPersisted()) {
      return t(".edit_contact", {name: presence(contact.name()) || `${contact.firstName()} ${contact.lastName()}`})
    }
  }

  onContactTypeChange = ({options}) => {
    const contactType = options?.[0]?.contactType

    this.setState({contactTypeIdentifier: contactType?.identifier() || null})
  }

  onSubmit = async () => {
    const {contact, t} = this.tt
    const formData = this.s.form.asObject()

    if (contact.isNewRecord()) {
      formData.contact.new_id = contact.id()
    }

    try {
      await contact.saveRaw(formData)
      FlashMessage.success(t(".the_contact_was_saved"))
      AppHistory.push(Routes.adminContactPath(contact.id()))
    } catch (error) {
      FlashMessage.errorResponse(error)
    }
  }
}))
